import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2, Quote, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "../components/Reveal";
import { Icon } from "../components/Icon";
import { CTASection } from "../components/shared/CTASection";
import {
  IMAGES, SERVICES, INDUSTRIES, WHY_CHOOSE, STATS, COMPANY, TEAM, AUDITGUARD,
} from "../data/content";

const Hero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden bg-navy text-white">
    <img src={IMAGES.heroJet} alt="Private jet on runway at sunset" className="absolute inset-0 h-full w-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/90 to-navy/50" />
    <div className="grain relative mx-auto grid max-w-7xl gap-10 px-5 py-28 lg:grid-cols-12 lg:px-8 lg:py-40">
      <div className="lg:col-span-8">
        <motion.span
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-aero"
        >
          Global Aviation Consulting · A division of {COMPANY.parentShort}
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-7 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl text-balance"
        >
          Aviation operations, safety & <span className="text-aero">AI transformation</span> — engineered for a global era.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-white/75 lg:text-lg"
        >
          {COMPANY.name} helps airlines, operators, MROs and government-related aviation bodies
          strengthen compliance, sharpen operations, and adopt AI responsibly — with a dedicated
          focus on the growth opportunity across Africa.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <Link to="/quote" data-testid="hero-quote-cta" className="inline-flex items-center justify-center gap-1.5 rounded-full bg-aero px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
            Request a Quote <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link to="/services" data-testid="hero-services-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10">
            Explore Services <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </div>

    <div className="relative border-t border-white/10 bg-navy/60 backdrop-blur">
      <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 px-5 lg:grid-cols-4 lg:px-8">
        {STATS.map((s, i) => (
          <div key={s.label} data-testid={`stat-${i}`} className="px-4 py-8 lg:px-8">
            <div className="font-display text-2xl font-extrabold text-aero lg:text-3xl">{s.value}</div>
            <div className="mt-1 text-xs text-white/60 lg:text-sm">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Positioning = () => (
  <section data-testid="positioning-section" className="bg-background py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Positioning</span>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl text-balance">
              A premium consultancy built for both the commercial market and the public sector.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground lg:text-lg">
              We bring the discipline of aviation safety and the ambition of modern transformation
              to every engagement. From audit readiness to AI governance, we help aviation
              organizations operate with confidence, credibility and control.
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {["Globally focused", "Future-facing AI expertise", "Commercial & government credibility", "Dedicated Africa growth lens"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm font-medium text-navy">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-brand" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMAGES.consulting} alt="Aviation advisory meeting" className="h-56 w-full rounded-2xl object-cover lg:h-72" />
              <img src={IMAGES.tower} alt="Airport control tower" className="mt-8 h-56 w-full rounded-2xl object-cover lg:h-72" />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const ServicesGrid = () => (
  <section data-testid="services-section" className="bg-secondary py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal>
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Launch Services</span>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
            Five service lines. One standard of excellence.
          </h2>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.05} className={`bg-white ${i === 0 ? "lg:col-span-2" : ""}`}>
            <Link
              to={`/services#${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="group flex h-full flex-col justify-between p-8 transition-colors hover:bg-secondary/60"
            >
              <div>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <Icon name={s.icon} className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold text-navy">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.summary}</p>
              </div>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand transition-transform group-hover:translate-x-1">
                Learn more <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyChoose = () => (
  <section data-testid="why-choose-section" className="bg-background py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal>
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Why Global-Elect</span>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
            The credibility of a parent company. The focus of a specialist.
          </h2>
        </div>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {WHY_CHOOSE.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.06}>
            <div className="group h-full rounded-2xl border border-border bg-white p-8 transition-transform hover:-translate-y-1 hover:shadow-xl">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-aero/10 text-aero">
                <Icon name={w.icon} className="h-5 w-5" />
              </span>
              <h3 className="mt-6 font-display text-lg font-bold text-navy">{w.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{w.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const IndustriesRibbon = () => {
  const items = [...INDUSTRIES, ...INDUSTRIES];
  return (
    <section data-testid="industries-ribbon" className="border-y border-border bg-secondary py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div className="max-w-xl">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Industries Served</span>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                Trusted across the aviation ecosystem.
              </h2>
            </div>
            <Link to="/industries" data-testid="industries-view-all" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:translate-x-1 transition-transform">
              View all industries <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
      <div className="relative mt-12 overflow-hidden">
        <div className="marquee-track gap-4 px-2">
          {items.map((ind, i) => (
            <div key={i} className="flex w-64 shrink-0 items-center gap-3 rounded-xl border border-border bg-white px-5 py-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand/10 text-brand">
                <Icon name={ind.icon} className="h-5 w-5" />
              </span>
              <span className="font-display text-sm font-semibold text-navy">{ind.title}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AfricaTeaser = () => (
  <section data-testid="africa-teaser" className="relative overflow-hidden bg-navy text-white">
    <img src={IMAGES.africa} alt="Aerial view over Africa" className="absolute inset-0 h-full w-full object-cover opacity-35" />
    <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
    <div className="grain relative mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
      <Reveal>
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Africa Growth Focus</span>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl text-balance">
          The next decade of aviation growth runs through Africa. We help you capture it — safely.
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 lg:text-lg">
          Rising demand, liberalization and infrastructure investment are opening a structural
          growth window. We pair operational maturity with compliance foundations so operators and
          public bodies can expand with confidence.
        </p>
        <Link to="/africa" data-testid="africa-learn-more" className="mt-9 inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10">
          Explore our Africa focus <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const GovernmentTeaser = () => (
  <section data-testid="government-teaser" className="bg-background py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-5">
          <Reveal><img src={IMAGES.airport} alt="Modern airport terminal" className="h-80 w-full rounded-3xl object-cover lg:h-[420px]" /></Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Government Capability</span>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl text-balance">
              Positioned for public-sector and government-related aviation work.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground lg:text-lg">
              From capability statements and proposal readiness to SOPs, manuals and compliance
              documentation, we help aviation organizations meet the standards that public-sector
              engagements demand — including U.S. government capability positioning where applicable.
            </p>
            <Link to="/government" data-testid="government-learn-more" className="mt-9 inline-flex items-center gap-1.5 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
              Government capability <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const CEOMessage = () => (
  <section data-testid="ceo-message-section" className="bg-secondary py-20 lg:py-28">
    <div className="mx-auto max-w-5xl px-5 lg:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-border bg-white p-8 shadow-sm lg:p-14">
          <Quote className="absolute right-8 top-8 h-16 w-16 text-brand/10" aria-hidden="true" />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Message from the CEO</span>
          <blockquote className="mt-6 font-display text-xl font-semibold leading-snug tracking-tight text-navy sm:text-2xl lg:text-3xl text-balance">
            &ldquo;{TEAM.ceoMessage}&rdquo;
          </blockquote>
          <div className="mt-8 flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand to-navy font-display text-lg font-extrabold text-white">
              {TEAM.leader.initials}
            </span>
            <div>
              <div className="font-display text-base font-bold text-navy">{TEAM.leader.name}</div>
              <div className="text-sm font-semibold uppercase tracking-wide text-aero">{TEAM.leader.title}</div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const AuditGuardTeaser = () => (
  <section data-testid="auditguard-teaser" className="relative overflow-hidden bg-navy text-white">
    <img src={IMAGES.ai} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
    <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/55" />
    <div className="grain relative mx-auto grid max-w-7xl gap-10 px-5 py-24 lg:grid-cols-12 lg:items-center lg:px-8 lg:py-28">
      <div className="lg:col-span-7">
        <Reveal>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-aero">
              <Sparkles className="h-3.5 w-3.5" /> Flagship AI Platform
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Now Live
            </span>
          </div>
          <h2 className="mt-6 max-w-2xl font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl text-balance">
            Meet {AUDITGUARD.name} — compliance, automated.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 lg:text-lg">
            {AUDITGUARD.intro} We're building it to serve flight schools, charter operators, airports,
            MROs, UAS companies and regional airlines.
          </p>
          <Link to="/ai-solutions" data-testid="auditguard-teaser-cta" className="mt-9 inline-flex items-center gap-1.5 rounded-full bg-aero px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
            Explore AI Solutions <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal delay={0.12}>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur lg:p-8">
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50">Platform components</h3>
            <ul className="mt-4 space-y-3">
              {AUDITGUARD.components.slice(0, 5).map((c) => (
                <li key={c.label} className="flex items-center gap-3 text-sm font-medium text-white/90">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-aero/15 text-aero">
                    <Icon name={c.icon} className="h-4 w-4" />
                  </span>
                  {c.label}
                </li>
              ))}
              <li className="pl-8 text-sm font-medium text-aero">+ more inside</li>
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Positioning />
      <ServicesGrid />
      <WhyChoose />
      <IndustriesRibbon />
      <AfricaTeaser />
      <GovernmentTeaser />
      <AuditGuardTeaser />
      <CEOMessage />
      <CTASection />
    </div>
  );
}
